import {
  Badge,
  Button,
  Card,
  Code,
  Group,
  Select,
  SimpleGrid,
  Skeleton,
  Stack,
  Table,
  Text,
  Title,
  Tooltip,
} from "@mantine/core";
import { ArrowRight } from "lucide-react";
import { BarChart } from "@mantine/charts";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { adminErrorMessage, adminGet } from "../../../api.ts";
import { AdminPageHeader } from "../../../components/AdminPageChrome.tsx";
import { BotTimezoneNotice } from "../../../components/BotTimezoneNotice.tsx";
import {
  StatsTrendControls,
  trendRangeLabel,
  type TrendRange,
} from "../../../components/StatsTrendControls.tsx";
import { AdminTable } from "../../../components/AdminTable.tsx";
import { TablePagination } from "../../../components/TablePagination.tsx";
import { useClientPagination } from "../../../hooks/useClientPagination.ts";
import {
  issueSeverityColor,
  SHORTLINK_ISSUE_META,
  type ShortlinkIssueKind,
} from "./shortlink-issue-shared.ts";

interface IssueBreakdownItem {
  readonly issueKind: ShortlinkIssueKind;
  readonly count: number;
  readonly pctOfIssues: number | null;
}

interface IssueSample {
  readonly issueKind: ShortlinkIssueKind;
  readonly landingUrl: string | null;
  readonly adId: string;
  readonly referer: string | null;
  readonly count: number;
}

interface RefererCount {
  readonly referer: string;
  readonly count: number;
}

interface ShortlinkDiagnosisResponse {
  readonly range: TrendRange;
  readonly days: number;
  readonly timezone: string;
  readonly statsDay: string;
  readonly filters: {
    readonly chatId: string | null;
    readonly adId: string | null;
  };
  readonly totalVisits: number;
  readonly issueVisits: number;
  readonly healthyVisits: number;
  readonly issueBreakdown: ReadonlyArray<IssueBreakdownItem>;
  readonly samples: ReadonlyArray<IssueSample>;
  readonly referers: ReadonlyArray<RefererCount>;
}

interface ChatOption {
  readonly chatId: number;
  readonly title: string | null;
  readonly chatToken: string;
}

interface ChatsResponse {
  readonly items: ReadonlyArray<ChatOption>;
}

type ShortlinkDiagnosisTabProps = {
  readonly botId: string;
  readonly onOpenVisits?: (filters: {
    readonly disposition?: string;
    readonly chatId?: string;
  }) => void;
};

export function ShortlinkDiagnosisTab(props: ShortlinkDiagnosisTabProps) {
  const { botId, onOpenVisits } = props;
  const [range, setRange] = useState<TrendRange>("today");
  const [chatId, setChatId] = useState<string | null>(null);

  const chatsQuery = useQuery({
    queryKey: ["analytics", botId, "chats"],
    queryFn: () => adminGet<ChatsResponse>(`/analytics/${botId}/chats`),
    staleTime: 60_000,
  });

  const chatOptions = (chatsQuery.data?.items ?? []).map((c) => ({
    value: String(c.chatId),
    label: c.title === null || c.title === "" ? String(c.chatId) : `${c.title} (${c.chatId})`,
  }));

  const filterQs = chatId === null ? "" : `&chatId=${encodeURIComponent(chatId)}`;

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["analytics", botId, "shortlink-diagnosis", range, chatId],
    queryFn: () =>
      adminGet<ShortlinkDiagnosisResponse>(
        `/analytics/${botId}/shortlink-diagnosis?range=${range}${filterQs}`,
      ),
    refetchInterval: 60_000,
  });

  const chartData =
    data?.issueBreakdown.map((it) => {
      const meta = SHORTLINK_ISSUE_META[it.issueKind];
      return {
        kind: meta.label,
        count: it.count,
        color: issueSeverityColor(meta.severity) + ".6",
      };
    }) ?? [];

  const samplesByKind = new Map<ShortlinkIssueKind, IssueSample[]>();
  for (const s of data?.samples ?? []) {
    const list = samplesByKind.get(s.issueKind) ?? [];
    list.push(s);
    samplesByKind.set(s.issueKind, list);
  }

  const refererItems = data?.referers ?? [];
  const {
    pageItems: pagedReferers,
    page: referersPage,
    setPage: setReferersPage,
    pageSize: referersPageSize,
    total: referersTotal,
    totalPages: referersTotalPages,
  } = useClientPagination(refererItems, `${range}|${chatId ?? ""}`);

  const openVisits = (issueKind: ShortlinkIssueKind) => {
    const disposition = SHORTLINK_ISSUE_META[issueKind].dispositionFilter;
    onOpenVisits?.({
      ...(disposition !== null ? { disposition } : {}),
      ...(chatId !== null ? { chatId } : {}),
    });
  };

  return (
    <Stack gap="md">
      <AdminPageHeader
        title="链接诊断"
        description="按访问 URL 与处置结果拆分问题类型（宏未替换、丢 fbclid、池满降级等）。健康访问不计入问题占比分母。"
        actions={
          data !== undefined ? (
            <BotTimezoneNotice
              timezone={data.timezone}
              statsDay={data.statsDay}
              range={data.range}
            />
          ) : undefined
        }
      />

      <Group justify="space-between" wrap="wrap" align="flex-end">
        <Group wrap="wrap" align="flex-end" gap="md">
          <StatsTrendControls
            range={range}
            granularity="day"
            onRangeChange={setRange}
            onGranularityChange={() => {}}
            hideGranularity
          />
          <Select
            size="xs"
            label="群组"
            placeholder="全部群组"
            data={chatOptions}
            value={chatId}
            onChange={setChatId}
            searchable
            clearable
            nothingFoundMessage="无匹配群组"
            disabled={chatsQuery.isLoading}
            w={260}
          />
        </Group>
      </Group>

      {isLoading ? (
        <Stack gap="md">
          <SimpleGrid cols={{ base: 2, sm: 4 }} spacing="md">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} h={80} radius="md" />
            ))}
          </SimpleGrid>
          <Skeleton h={320} radius="md" />
        </Stack>
      ) : isError ? (
        <Text c="red" size="sm" ta="center" py="lg">
          {adminErrorMessage(error)}
        </Text>
      ) : (
        <>
          <SimpleGrid cols={{ base: 2, sm: 4 }} spacing="md">
            <KpiCard label="总访问" value={data?.totalVisits ?? 0} />
            <KpiCard label="健康访问" value={data?.healthyVisits ?? 0} />
            <KpiCard label="问题访问" value={data?.issueVisits ?? 0} />
            <KpiCard
              label="问题占比"
              value={
                data !== undefined && data.totalVisits > 0
                  ? `${Math.round((data.issueVisits / data.totalVisits) * 1000) / 10}%`
                  : "-"
              }
            />
          </SimpleGrid>

          <Card shadow="sm" padding="lg" radius="md" withBorder>
            <Text fw={600} size="lg" mb="md">
              {trendRangeLabel(data?.range ?? range)}问题分布
            </Text>
            {chartData.length === 0 ? (
              <Text c="dimmed" size="sm" ta="center" py="lg">
                当前时间范围内无问题访问
              </Text>
            ) : (
              <BarChart
                h={Math.max(240, chartData.length * 36)}
                data={chartData}
                dataKey="kind"
                orientation="vertical"
                withTooltip
                series={[{ name: "count", label: "次数", color: "red.6" }]}
              />
            )}
          </Card>

          <Card shadow="sm" padding="lg" radius="md" withBorder>
            <Text fw={600} size="lg" mb="md">
              问题明细与处理建议
            </Text>
            {(data?.issueBreakdown.length ?? 0) === 0 ? (
              <Text c="dimmed" size="sm">
                暂无问题访问。投放链接参数与池子运行正常。
              </Text>
            ) : (
              <Stack gap="md">
                {data?.issueBreakdown.map((item) => {
                  const meta = SHORTLINK_ISSUE_META[item.issueKind];
                  const samples = samplesByKind.get(item.issueKind) ?? [];
                  return (
                    <Card key={item.issueKind} withBorder padding="md" radius="md">
                      <Group justify="space-between" mb="xs" wrap="wrap">
                        <Group gap="sm">
                          <Badge color={issueSeverityColor(meta.severity)} variant="light">
                            {meta.label}
                          </Badge>
                          <Text fw={600}>{item.count.toLocaleString()} 次</Text>
                          {item.pctOfIssues !== null ? (
                            <Text size="sm" c="dimmed">
                              占问题访问 {item.pctOfIssues}%
                            </Text>
                          ) : null}
                        </Group>
                        {onOpenVisits !== undefined && meta.dispositionFilter !== null ? (
                          <Button
                            variant="subtle"
                            size="xs"
                            rightSection={<ArrowRight size={14} />}
                            onClick={() => openVisits(item.issueKind)}
                          >
                            查看访问记录
                          </Button>
                        ) : null}
                      </Group>
                      <Text size="sm" c="dimmed" mb="sm">
                        {meta.hint}
                      </Text>
                      {samples.length > 0 ? (
                        <AdminTable minWidth={800}>
                          <Table.Thead>
                            <Table.Tr>
                              <Table.Th>次数</Table.Th>
                              <Table.Th>广告 ID</Table.Th>
                              <Table.Th>访问 URL</Table.Th>
                              <Table.Th>Referer</Table.Th>
                            </Table.Tr>
                          </Table.Thead>
                          <Table.Tbody>
                            {samples.map((s, idx) => (
                              <Table.Tr key={`${s.landingUrl ?? ""}-${idx}`}>
                                <Table.Td>{s.count.toLocaleString()}</Table.Td>
                                <Table.Td>
                                  {s.adId !== "" ? (
                                    <Code>{s.adId}</Code>
                                  ) : (
                                    <Text size="xs" c="dimmed">
                                      -
                                    </Text>
                                  )}
                                </Table.Td>
                                <Table.Td>
                                  <TruncatedCode value={s.landingUrl} />
                                </Table.Td>
                                <Table.Td>
                                  <TruncatedCode value={s.referer} maxWidth={140} />
                                </Table.Td>
                              </Table.Tr>
                            ))}
                          </Table.Tbody>
                        </AdminTable>
                      ) : null}
                    </Card>
                  );
                })}
              </Stack>
            )}
          </Card>

          {refererItems.length > 0 ? (
            <Card shadow="sm" padding="lg" radius="md" withBorder>
              <Text fw={600} size="lg" mb="md">
                问题访问 Referer Top（不含爬虫）
              </Text>
              <AdminTable minWidth={700}>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Referer</Table.Th>
                    <Table.Th>次数</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {pagedReferers.map((r) => (
                    <Table.Tr key={r.referer}>
                      <Table.Td>
                        <Code>{r.referer}</Code>
                      </Table.Td>
                      <Table.Td>{r.count.toLocaleString()}</Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </AdminTable>
              <TablePagination
                page={referersPage}
                pageSize={referersPageSize}
                total={referersTotal}
                totalPages={referersTotalPages}
                onPageChange={setReferersPage}
              />
            </Card>
          ) : null}
        </>
      )}
    </Stack>
  );
}

function KpiCard(props: { readonly label: string; readonly value: string | number }) {
  const display = typeof props.value === "number" ? props.value.toLocaleString() : props.value;
  return (
    <Card shadow="xs" padding="md" radius="md" withBorder>
      <Text size="xs" c="dimmed" mb={4}>
        {props.label}
      </Text>
      <Title order={3}>{display}</Title>
    </Card>
  );
}

function TruncatedCode(props: { readonly value: string | null; readonly maxWidth?: number }) {
  const { value, maxWidth = 360 } = props;
  if (value === null || value === "") {
    return (
      <Text size="xs" c="dimmed">
        -
      </Text>
    );
  }
  return (
    <Tooltip label={value} multiline maw={480} withArrow>
      <Code
        style={{
          display: "block",
          maxWidth,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          cursor: "default",
        }}
      >
        {value}
      </Code>
    </Tooltip>
  );
}
